import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function HessLawCalculator() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Chemistry", url: "https://calclab.app/chemistry" },
      { name: "Hess's Law Calculator", url: "https://calclab.app/calculator/hess-law" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Hess's Law Calculator",
      "Calculate total ΔH from multiple reaction steps using Hess's Law. Free online thermochemistry calculator.",
      "https://calclab.app/calculator/hess-law",
      lang
    )
  ]);
  const [steps, setSteps] = useState([{ enthalpy: "", coefficient: "1" }]);
  const [result, setResult] = useState<number | null>(null);

  const addStep = () => {
    setSteps([...steps, { enthalpy: "", coefficient: "1" }]);
  };

  const updateStep = (index: number, field: "enthalpy" | "coefficient", value: string) => {
    const newSteps = [...steps];
    newSteps[index][field] = value;
    setSteps(newSteps);
  };

  const calculate = () => {
    let totalEnthalpy = 0;
    
    for (const step of steps) {
      const h = parseFloat(step.enthalpy);
      const coeff = parseFloat(step.coefficient);
      
      if (isNaN(h) || isNaN(coeff)) {
        toast({
          title: "Invalid Input",
          description: "Please enter valid numbers for all steps.",
          variant: "destructive",
        });
        return;
      }
      
      totalEnthalpy += h * coeff;
    }

    setResult(totalEnthalpy);

    toast({
      title: "Calculation Complete",
      description: `Total ΔH = ${totalEnthalpy.toFixed(2)} kJ`,
    });
  };

  const reset = () => {
    setSteps([{ enthalpy: "", coefficient: "1" }]);
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Hess's Law Calculator" 
      calculatorPath="/calculator/hess-law"
      category="chemistry"
      subcategory="Thermochemistry"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Hess's Law Calculator</CardTitle>
            <CardDescription>
              Calculate total ΔH from multiple reaction steps
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              {steps.map((step, index) => (
                <div key={index} className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Step {index + 1} ΔH (kJ)</Label>
                    <Input
                      type="number"
                      step="any"
                      value={step.enthalpy}
                      onChange={(e) => updateStep(index, "enthalpy", e.target.value)}
                      placeholder="Enthalpy change"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Coefficient</Label>
                    <Input
                      type="number"
                      step="any"
                      value={step.coefficient}
                      onChange={(e) => updateStep(index, "coefficient", e.target.value)}
                      placeholder="Multiply by"
                    />
                  </div>
                </div>
              ))}
              <Button onClick={addStep} variant="outline" className="w-full">
                Add Step
              </Button>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result !== null && (
              <>
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">Total ΔH</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-start justify-between gap-4">
                      <p className="text-2xl font-bold break-all overflow-wrap-anywhere flex-1">{result.toFixed(2)} kJ</p>
                      <CopyResultButton value={`${result.toFixed(2)} kJ`} />
                    </div>
                  </CardContent>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["hess-law"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Hess's Law</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <p>
                  The total enthalpy change is the sum of all individual steps, 
                  regardless of the pathway taken. Multiply each ΔH by its coefficient 
                  if reactions are reversed or multiplied.
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
